import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, Spinner } from '@fluentui/react-components';
import { Add16Regular, Delete16Regular, Dismiss24Regular, Edit16Regular, Search16Regular, Tag20Regular } from '@fluentui/react-icons';

import * as ComponentFunctions from './ContractConceptCategories.Functions';
import { CategoryDraft, DraftErrors, draftOf, filterCategories, hasErrors, isInUseError, toPayload, validateDraft } from './ContractConceptCategories.Logic';
import { ContractConceptCategory } from '../../../../models/Entities/Contracts/ContractConceptCategory';

import './ContractConceptCategories.Styles.css';

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; category: ContractConceptCategory };

/**
 * Alta, edición y baja de las categorías de concepto de contrato del tenant.
 * El formulario va dentro del mismo drawer, arriba de la lista: no hay un
 * segundo panel encima.
 */
const ContractConceptCategoriesDrawer = (props: ComponentFunctions.Props) => {
    const [mode, setMode] = useState<Mode>({ kind: 'idle' });
    const [draft, setDraft] = useState<CategoryDraft>(draftOf());
    const [touched, setTouched] = useState(false);
    const [search, setSearch] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | undefined>(undefined);
    const nameRef = useRef<HTMLInputElement | null>(null);

    // Al abrir: lista fresca y sin restos de la vez anterior.
    useEffect(() => {
        if (!props.isOpen) return;
        props.ResetContractConceptCategoryFlags();
        props.GetAllContractConceptCategories(true);
        setMode({ kind: 'idle' });
        setSearch('');
        setConfirmDeleteId(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen]);

    // Guardado: el formulario se cierra solo.
    useEffect(() => {
        if (props.savedOk && mode.kind !== 'idle') {
            setMode({ kind: 'idle' });
            setDraft(draftOf());
            setTouched(false);
            props.ResetContractConceptCategoryFlags();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.savedOk]);

    useEffect(() => {
        if (props.deletedOk) setConfirmDeleteId(undefined);
    }, [props.deletedOk]);

    useEffect(() => {
        if (mode.kind !== 'idle') nameRef.current?.focus();
    }, [mode]);

    const editingId = mode.kind === 'edit' ? mode.category.id : undefined;
    const errors: DraftErrors = useMemo(() => validateDraft(draft, props.Categories, editingId), [draft, props.Categories, editingId]);
    const visible = useMemo(() => filterCategories(props.Categories, search), [props.Categories, search]);

    const startNew = () => { props.ResetContractConceptCategoryFlags(); setDraft(draftOf()); setTouched(false); setMode({ kind: 'new' }); };
    const startEdit = (category: ContractConceptCategory) => { props.ResetContractConceptCategoryFlags(); setDraft(draftOf(category)); setTouched(false); setMode({ kind: 'edit', category }); };
    const cancelForm = () => { setMode({ kind: 'idle' }); setDraft(draftOf()); setTouched(false); props.ResetContractConceptCategoryFlags(); };

    const save = () => {
        setTouched(true);
        if (hasErrors(errors) || props.isSaving) return;
        const payload = toPayload(draft);
        if (mode.kind === 'edit') props.UpdateContractConceptCategory({ ...mode.category, ...payload });
        else props.AddContractConceptCategory(payload);
    };

    const deleteError = props.failOnDeleting && props.deletingId !== undefined
        ? (isInUseError(props.error) ? 'No se puede eliminar: hay conceptos de contrato que la usan.' : (props.error?.ErrorMessage || 'No se pudo eliminar la categoría.'))
        : undefined;

    const formTitle = mode.kind === 'edit' ? 'Editar categoría' : 'Nueva categoría';

    return (
        <OverlayDrawer open={props.isOpen} position="end" size="medium" className="ccc-drawer" onOpenChange={(_, data) => { if (!data.open) props.onClose(); }}>
            <DrawerHeader className="ccc-header">
                <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Cerrar" icon={<Dismiss24Regular />} onClick={props.onClose} />}>
                    <span className="ccc-title">Categorías de conceptos</span>
                </DrawerHeaderTitle>
                <p className="ccc-intro">Agrupan los conceptos del contrato para filtrarlos y resumirlos. Una categoría en uso no se puede eliminar.</p>
                <div className="ccc-toolbar">
                    <label className="ccc-search">
                        <Search16Regular />
                        <input
                            type="search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Buscar..."
                            aria-label="Buscar categoría"
                        />
                    </label>
                    <span className="ccc-pill">{props.Categories.length} {props.Categories.length === 1 ? 'categoría' : 'categorías'}</span>
                    <button type="button" className="ccc-btn ccc-btn--primary" onClick={startNew} disabled={mode.kind === 'new'}>
                        <Add16Regular /> Nueva categoría
                    </button>
                </div>
            </DrawerHeader>

            <DrawerBody className="ccc-body">
                {mode.kind !== 'idle' && (
                    <section className="ccc-form" aria-label={formTitle}>
                        <span className="ccc-form__title">{formTitle}</span>
                        <div className="ccc-field">
                            <label className="ccc-label" htmlFor="ccc-name">Nombre <span className="ccc-required">*</span></label>
                            <input
                                id="ccc-name"
                                ref={nameRef}
                                className={`ccc-input${touched && errors.name ? ' ccc-input--error' : ''}`}
                                value={draft.name}
                                maxLength={200}
                                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                                onKeyDown={(event) => { if (event.key === 'Enter') save(); if (event.key === 'Escape') cancelForm(); }}
                                placeholder="Ej. Visitas, Procedimientos, Insumos"
                            />
                            {touched && errors.name ? <span className="ccc-error">{errors.name}</span> : <span className="ccc-hint">Como se va a ver en los conceptos y en los filtros.</span>}
                        </div>
                        <div className="ccc-field">
                            <label className="ccc-label" htmlFor="ccc-description">Descripción</label>
                            <textarea
                                id="ccc-description"
                                className={`ccc-input ccc-input--area${touched && errors.description ? ' ccc-input--error' : ''}`}
                                value={draft.description}
                                maxLength={500}
                                rows={2}
                                onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                                placeholder="Opcional"
                            />
                            {touched && errors.description && <span className="ccc-error">{errors.description}</span>}
                        </div>
                        {props.failOnSaving && (
                            <div className="ccc-banner ccc-banner--error">
                                <strong>No se pudo guardar.</strong> {props.error?.ErrorMessage || 'Probá de nuevo.'}
                                {props.error?.Errors && props.error.Errors.length > 0 && <ul>{props.error.Errors.map((item) => <li key={item}>{item}</li>)}</ul>}
                            </div>
                        )}
                        <div className="ccc-form__actions">
                            <button type="button" className="ccc-btn" onClick={cancelForm} disabled={props.isSaving}>Cancelar</button>
                            <button type="button" className="ccc-btn ccc-btn--primary" onClick={save} disabled={props.isSaving || (touched && hasErrors(errors))}>
                                {props.isSaving && <Spinner size="extra-tiny" />} {mode.kind === 'edit' ? 'Guardar cambios' : 'Crear categoría'}
                            </button>
                        </div>
                    </section>
                )}

                {deleteError && <div className="ccc-banner ccc-banner--error">{deleteError}</div>}

                {props.isLoading && props.Categories.length === 0 && (
                    <div className="ccc-muted ccc-loading"><Spinner size="extra-tiny" /> Cargando categorías…</div>
                )}

                {/* Un GET fallido no es "no hay categorías": se dice y se puede reintentar */}
                {!props.isLoading && props.failOnLoading && (
                    <div className="ccc-banner ccc-banner--error">
                        <strong>No se pudieron cargar las categorías.</strong> {props.error?.ErrorMessage || ''}
                        <div className="ccc-form__actions" style={{ marginTop: 8 }}>
                            <button type="button" className="ccc-btn" onClick={() => props.GetAllContractConceptCategories(true)}>Reintentar</button>
                        </div>
                    </div>
                )}

                {!props.isLoading && props.hasLoaded && !props.failOnLoading && props.Categories.length === 0 && mode.kind === 'idle' && (
                    <div className="ccc-empty">
                        <Tag20Regular />
                        <span className="ccc-empty__title">Todavía no hay categorías</span>
                        <span className="ccc-muted">Creá la primera para empezar a agrupar los conceptos del contrato.</span>
                        <button type="button" className="ccc-btn ccc-btn--primary" onClick={startNew}><Add16Regular /> Nueva categoría</button>
                    </div>
                )}

                {props.Categories.length > 0 && visible.length === 0 && (
                    <div className="ccc-muted">Ninguna categoría coincide con “{search}”.</div>
                )}

                {visible.length > 0 && (
                    <ul className="ccc-list">
                        {visible.map((category) => {
                            const isEditing = editingId === category.id;
                            const isDeletingThis = props.isDeleting && props.deletingId === category.id;
                            const askingDelete = confirmDeleteId === category.id;
                            return (
                                <li key={category.id} className={`ccc-row${isEditing ? ' ccc-row--editing' : ''}`}>
                                    <span className="ccc-row__icon" aria-hidden><Tag20Regular /></span>
                                    <div className="ccc-row__body">
                                        <span className="ccc-row__name" title={category.name}>{category.name}</span>
                                        {category.description && <span className="ccc-row__desc" title={category.description}>{category.description}</span>}
                                        {askingDelete && (
                                            <div className="ccc-confirm">
                                                <span>¿Eliminar “{category.name}”?</span>
                                                <button type="button" className="ccc-btn ccc-btn--danger" disabled={isDeletingThis} onClick={() => props.DeleteContractConceptCategory(category.id)}>
                                                    {isDeletingThis && <Spinner size="extra-tiny" />} Eliminar
                                                </button>
                                                <button type="button" className="ccc-btn" disabled={isDeletingThis} onClick={() => setConfirmDeleteId(undefined)}>Cancelar</button>
                                            </div>
                                        )}
                                    </div>
                                    {!askingDelete && (
                                        <span className="ccc-row__actions">
                                            <button type="button" className="ccc-icon-btn" title="Editar" aria-label={`Editar ${category.name}`} onClick={() => startEdit(category)} disabled={props.isDeleting}>
                                                <Edit16Regular />
                                            </button>
                                            <button type="button" className="ccc-icon-btn ccc-icon-btn--danger" title="Eliminar" aria-label={`Eliminar ${category.name}`} onClick={() => { props.ResetContractConceptCategoryFlags(); setConfirmDeleteId(category.id); }} disabled={props.isDeleting}>
                                                <Delete16Regular />
                                            </button>
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </DrawerBody>

            <DrawerFooter className="ccc-footer">
                <button type="button" className="ccc-btn" onClick={props.onClose}>Cerrar</button>
            </DrawerFooter>
        </OverlayDrawer>
    );
};

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(ContractConceptCategoriesDrawer as any) as React.ComponentType<ComponentFunctions.IProps>;
